import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { Layout } from './Home';
import PageHero from '../components/PageHero';
import { serviceContent } from '../data/pages';

const ServicePage = () => {
  const { slug } = useParams();

  // If no slug provided, show services listing
  if (!slug) {
    return (
      <Layout>
        <PageHero
          eyebrow="Our Services & Solutions"
          title="A global conglomerate with international alliances."
          description="Explore our verticals across bullion, corporate finance and capital markets — each backed by decades of expertise and a global partner network."
          image={Object.values(serviceContent)[0].image}
        />
        <section className="bg-[#e2ede6] py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(serviceContent).map(([key, s]) => (
              <Link
                key={key}
                to={`/services/${key}`}
                className="group bg-white rounded-2xl overflow-hidden border border-black/5 hover:shadow-xl transition-all"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={s.image} alt={s.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <span className="text-[10px] font-semibold tracking-[0.25em] uppercase text-[#0f5c3d]">{s.eyebrow}</span>
                  <h3
                    className="mt-2 text-2xl text-[#0b1a2e] group-hover:text-[#0f5c3d] transition-colors"
                    style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}
                  >
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm text-[#0b1a2e]/70 leading-relaxed line-clamp-3">{s.description}</p>
                  <div className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[#0f5c3d]">
                    Learn more <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </Layout>
    );
  }

  const svc = serviceContent[slug];

  if (!svc) return <Navigate to="/services" replace />;

  const others = Object.entries(serviceContent).filter(([k]) => k !== slug);

  return (
    <Layout>
      <PageHero
        eyebrow={svc.eyebrow}
        title={svc.title}
        description={svc.description}
        image={svc.image}
      />

      <section className="bg-[#e2ede6] py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">What We Offer</span>
            <h2
              className="mt-3 text-4xl lg:text-5xl text-[#0b1a2e] leading-[1.05]"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}
            >
              {svc.title} services built for global scale.
            </h2>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {svc.highlights.map((h) => (
                <div key={h.title} className="bg-white p-5 rounded-xl border border-[#0f5c3d]/10">
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 shrink-0 w-6 h-6 rounded-full bg-[#0f5c3d]/10 text-[#0f5c3d] flex items-center justify-center">
                      <Check className="w-3.5 h-3.5" />
                    </span>
                    <div>
                      <h3 className="font-semibold text-[#0b1a2e]">{h.title}</h3>
                      <p className="mt-1 text-sm text-[#0b1a2e]/70 leading-relaxed">{h.text}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <Link
              to="/contact"
              className="mt-10 inline-flex items-center gap-3 px-6 py-3.5 rounded-full bg-[#0b1a2e] text-white hover:bg-[#0f5c3d] transition-colors"
            >
              <span className="text-sm font-medium tracking-wide">Talk to our team</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <aside className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3] shadow-lg">
              <img src={svc.image} alt={svc.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0b1a2e]/40 to-transparent" />
            </div>
            <div className="mt-6 p-6 bg-[#0b1a2e] rounded-2xl text-white">
              <h3
                className="text-2xl"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}
              >
                Regulated. Global. Trusted.
              </h3>
              <p className="mt-2 text-sm text-white/75 leading-relaxed">
                Our teams operate under global compliance frameworks and partner with tier-1
                counterparties across geographies.
              </p>
            </div>
          </aside>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24 border-t border-[#0f5c3d]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Other Services</span>
          <h2
            className="mt-3 text-3xl lg:text-4xl text-[#0b1a2e]"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}
          >
            Explore more of what we do.
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {others.map(([key, s]) => (
              <Link
                key={key}
                to={`/services/${key}`}
                className="group relative block rounded-2xl overflow-hidden aspect-[16/7] shadow hover:shadow-xl transition-all"
              >
                <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#0b1a2e]/85 via-[#0b1a2e]/55 to-[#0f5c3d]/40" />
                <div className="relative h-full flex flex-col justify-center items-center text-center px-6 text-white">
                  <h3
                    className="text-2xl lg:text-3xl leading-tight"
                    style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}
                  >
                    {s.title}
                  </h3>
                  <div className="mt-3 inline-flex items-center gap-2 text-sm font-medium opacity-90 group-hover:gap-3 transition-all">
                    Learn More <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ServicePage;
